import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { formatNZD } from "@/lib/format";
import { toast } from "sonner";
import {
  Loader2,
  FileText,
  UploadCloud,
  X,
  ScanLine,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

/**
 * Iter 726 §A — dealer self-upload stock sheet (My Ads).
 * Step 1 upload a PDF (≤10 MB) → POST /classifieds/stock-sheet/preview
 * Step 2 review: three groups (New / Price changes / mark SOLD), every
 *        row tappable to untick; unchanged rows collapse to one line.
 * Step 3 POST /classifieds/stock-sheet/apply → result sheet. New ads
 *        land as "Under review"; price + sold apply immediately.
 */
export default function StockSheetDialog({ open, onClose }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null); // step 2 data
  const [result, setResult] = useState(null); // step 3 data
  // Selections: indexes of preview.items for new/price, ids for sold.
  const [pickedNew, setPickedNew] = useState([]);
  const [pickedPrice, setPickedPrice] = useState([]);
  const [pickedSold, setPickedSold] = useState([]);

  const reset = () => {
    setFileName("");
    setPreview(null);
    setResult(null);
    setPickedNew([]);
    setPickedPrice([]);
    setPickedSold([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const previewMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      // 15–40 s typical; give it headroom. No manual Content-Type.
      const { data } = await sharedApi.post("/classifieds/stock-sheet/preview", fd, {
        timeout: 120000,
      });
      return data;
    },
    onSuccess: (d) => {
      setPreview(d);
      // Everything ticked by default (sold too, per the work order).
      setPickedNew(d.items.map((it, i) => (it.change === "new" ? i : -1)).filter((i) => i >= 0));
      setPickedPrice(d.items.map((it, i) => (it.change === "price" ? i : -1)).filter((i) => i >= 0));
      setPickedSold((d.sold || []).map((s) => s.listing_id));
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (err?.code === "ECONNABORTED" || !err?.response) {
        toast.error("Couldn't reach the server — please try again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't read that PDF — please try again.");
      }
    },
  });

  const applyMut = useMutation({
    mutationFn: async () => {
      const body = {
        source_label: preview?.source?.pdf || fileName || "stock sheet",
        new: pickedNew.map((i) => ({
          listing: preview.items[i].listing,
          images: preview.items[i].images || [],
          page: preview.items[i].page,
        })),
        price_updates: pickedPrice.map((i) => ({
          listing_id: preview.items[i].match?.listing_id,
          price: preview.items[i].match?.new_price,
        })),
        sold: pickedSold,
      };
      const { data } = await sharedApi.post("/classifieds/stock-sheet/apply", body, {
        timeout: 60000,
      });
      return data;
    },
    onSuccess: (d) => {
      setResult(d);
      qc.invalidateQueries({ queryKey: ["my-ads"] });
      qc.invalidateQueries({ queryKey: ["subscription-me"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't apply the changes — please try again.");
    },
  });

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      toast.error("Please choose a PDF file.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("That PDF is over 10 MB — please use a smaller file.");
      return;
    }
    setFileName(f.name);
    previewMut.mutate(f);
  };

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const groups = useMemo(() => {
    const items = preview?.items || [];
    return {
      news: items.map((it, i) => ({ it, i })).filter(({ it }) => it.change === "new"),
      prices: items.map((it, i) => ({ it, i })).filter(({ it }) => it.change === "price"),
      unchanged: items.filter((it) => it.change === "unchanged").length,
      sold: preview?.sold || [],
      failed: preview?.failed || [],
    };
  }, [preview]);

  if (!open) return null;

  const applyLabel = [
    pickedNew.length ? `${pickedNew.length} new` : null,
    pickedPrice.length ? `${pickedPrice.length} price update${pickedPrice.length === 1 ? "" : "s"}` : null,
    pickedSold.length ? `${pickedSold.length} sold` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const nothingPicked = !pickedNew.length && !pickedPrice.length && !pickedSold.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4"
      data-testid="stock-sheet-dialog"
    >
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-soft px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <FileText className="w-5 h-5 text-[color:var(--brand-blue)] shrink-0" />
            <div className="min-w-0">
              <h2 className="font-display text-lg font-bold text-slate-900 truncate">
                Update from stock sheet
              </h2>
              {fileName && <p className="text-xs text-slate-400 truncate">{fileName}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            data-testid="stock-sheet-close"
            className="w-8 h-8 rounded-full grid place-items-center text-slate-400 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step 3 — result */}
        {result && (
          <div className="p-5" data-testid="stock-sheet-result">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 mb-3">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">
              {[
                result.created?.length
                  ? `${result.created.length} ad${result.created.length === 1 ? "" : "s"} submitted for review (usually a few hours)`
                  : null,
                result.price_updated ? `${result.price_updated} price updated` : null,
                result.sold ? `${result.sold} marked sold` : null,
              ]
                .filter(Boolean)
                .join(" · ") || "Nothing to apply"}
            </h3>
            {(result.failed || []).length > 0 && (
              <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 space-y-1.5" data-testid="stock-sheet-result-failed">
                {result.failed.map((f, i) => (
                  <p key={i} className="text-sm text-red-700">
                    <span className="font-semibold">{f.title}:</span> {f.error}
                    {/limit|plan|upgrade/i.test(String(f.error)) && (
                      <>
                        {" "}
                        <a href="/plan-and-pricing" className="font-bold underline">
                          Upgrade
                        </a>
                      </>
                    )}
                  </p>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={close}
              data-testid="stock-sheet-done-btn"
              className="mt-6 w-full rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {/* Step 1 — upload */}
        {!result && !preview && (
          <div className="p-5">
            {previewMut.isPending ? (
              <div className="py-12 text-center" data-testid="stock-sheet-reading">
                <Loader2 className="w-8 h-8 animate-spin text-[color:var(--brand-blue)] mx-auto" />
                <p className="mt-4 text-sm font-semibold text-slate-700">
                  Reading your stock sheet… one ad per vehicle
                </p>
                <p className="mt-1 text-xs text-slate-400">This can take up to 40 seconds.</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="stock-sheet-upload-btn"
                className="w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-[color:var(--brand-blue)] bg-slate-50 py-12 text-center transition-colors"
              >
                <UploadCloud className="w-8 h-8 text-slate-400 mx-auto" />
                <p className="mt-3 text-sm font-bold text-slate-700">Choose your stock sheet PDF</p>
                <p className="mt-1 text-xs text-slate-400">
                  Brochure or stock list · up to 10 MB · we create one ad per vehicle
                </p>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              data-testid="stock-sheet-file-input"
              onChange={onPickFile}
            />
          </div>
        )}

        {/* Step 2 — review */}
        {!result && preview && (
          <div className="p-5 space-y-5">
            {preview.source?.ocr && (
              <p
                className="flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-3"
                data-testid="stock-sheet-ocr-banner"
              >
                <ScanLine className="w-4 h-4 mt-0.5 shrink-0" />
                Scanned PDF — read automatically, please check prices and kms.
              </p>
            )}

            {groups.news.length > 0 && (
              <ReviewGroup
                title={`New vehicles (${groups.news.length})`}
                subtitle="Will be submitted for review"
              >
                {groups.news.map(({ it, i }) => (
                  <Row
                    key={i}
                    testId={`stock-sheet-row-new-${i}`}
                    checked={pickedNew.includes(i)}
                    onToggle={() => toggle(pickedNew, setPickedNew, i)}
                    title={it.listing?.title || `Page ${it.page}`}
                    sub={`${formatNZD(it.listing?.price)} · ${(it.images || []).length} photo${(it.images || []).length === 1 ? "" : "s"}`}
                  />
                ))}
              </ReviewGroup>
            )}

            {groups.prices.length > 0 && (
              <ReviewGroup
                title={`Price changes (${groups.prices.length})`}
                subtitle="Updates your live ad straight away"
              >
                {groups.prices.map(({ it, i }) => (
                  <Row
                    key={i}
                    testId={`stock-sheet-row-price-${i}`}
                    checked={pickedPrice.includes(i)}
                    onToggle={() => toggle(pickedPrice, setPickedPrice, i)}
                    title={it.match?.title || it.listing?.title}
                    sub={`${formatNZD(it.match?.old_price)} → ${formatNZD(it.match?.new_price)}`}
                  />
                ))}
              </ReviewGroup>
            )}

            {groups.sold.length > 0 && (
              <ReviewGroup
                title={`Not in this sheet — mark SOLD (${groups.sold.length})`}
                subtitle="Hidden from buyers, shows under Sold"
              >
                {groups.sold.map((s) => (
                  <Row
                    key={s.listing_id}
                    testId={`stock-sheet-row-sold-${s.listing_id}`}
                    checked={pickedSold.includes(s.listing_id)}
                    onToggle={() => toggle(pickedSold, setPickedSold, s.listing_id)}
                    title={s.title}
                    sub={formatNZD(s.price)}
                  />
                ))}
              </ReviewGroup>
            )}

            {groups.unchanged > 0 && (
              <p className="text-xs text-slate-400" data-testid="stock-sheet-unchanged-note">
                {groups.unchanged} already listed, unchanged.
              </p>
            )}

            {groups.failed.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid="stock-sheet-preview-failed">
                {groups.failed.map((f, i) => (
                  <p key={i} className="flex items-start gap-1.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Page {f.page}: {f.error}
                  </p>
                ))}
              </div>
            )}

            <div className="sticky bottom-0 bg-white border-t border-soft -mx-5 px-5 pt-3 pb-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={reset}
                disabled={applyMut.isPending}
                data-testid="stock-sheet-restart-btn"
                className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
              >
                Choose another PDF
              </button>
              <button
                type="button"
                onClick={() => applyMut.mutate()}
                disabled={applyMut.isPending || nothingPicked}
                data-testid="stock-sheet-apply-btn"
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-card transition-colors ${
                  applyMut.isPending || nothingPicked ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {applyMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {nothingPicked ? "Nothing selected" : `Apply: ${applyLabel}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewGroup({ title, subtitle, children }) {
  return (
    <section>
      <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      <ul className="mt-2 divide-y divide-subtle rounded-xl border border-soft overflow-hidden">
        {children}
      </ul>
    </section>
  );
}

function Row({ checked, onToggle, title, sub, testId }) {
  return (
    <li>
      <label
        className="flex items-center gap-3 px-3.5 py-2.5 bg-white hover:bg-slate-50 cursor-pointer select-none"
        data-testid={testId}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-4 h-4 accent-[color:var(--brand-blue)] shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-semibold clamp-1 ${checked ? "text-slate-900" : "text-slate-400 line-through"}`}>
            {title}
          </span>
          {sub && <span className="block text-xs text-slate-400">{sub}</span>}
        </span>
      </label>
    </li>
  );
}
